import { Injectable } from '@angular/core';
import { State, Action, Selector, StateContext } from '@ngxs/store';
import {
  CarrierShortNamesLoad,
  PartnerItemApprove,
  PartnerItemCreate,
  PartnerItemDelete,
  PartnerItemInitialState,
  PartnerItemLoad,
  PartnerItemUpdate,
} from './partner-item.actions';
import { PartnerModel } from '../models/partner.model';
import { catchError, map, take } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { NotificationService } from '../../../../../common/services/notification/notification.service';
import { PartnerItemService } from './partner-item.service';
import { NavigateUserService } from '../../../../services/navigate-user.service';
import { Error, CustomHttpErrorResponse } from '../../../../../common';
import { tap } from 'rxjs/operators';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';

export interface PartnerItemStateModel extends PartnerModel {
  loaded: boolean;
  shortNames: string[];
}

const initialState: PartnerItemStateModel = {
  ...new PartnerModel(),
  loaded: false,
  shortNames: [],
};

@State<PartnerItemStateModel>({
  name: 'partnerItem',
  defaults: initialState,
})
@Injectable()
export class PartnerItemState {
  constructor(
    private partnerItemService: PartnerItemService,
    private navigateUserService: NavigateUserService,
    private notificationService: NotificationService,
  ) {}

  @Selector()
  static getState(state: PartnerItemStateModel): PartnerItemStateModel {
    return state;
  }

  @Selector()
  static getPartnerId(state: PartnerItemStateModel): number {
    return state.id;
  }

  @Selector()
  static getPartnerName(state: PartnerItemStateModel): string {
    return state.name;
  }

  @Selector()
  static getLoaded(state: PartnerItemStateModel): boolean {
    return state.loaded;
  }

  @Selector()
  static getShortNames(state: PartnerItemStateModel): string[] {
    return state.shortNames.filter((s: string) => s !== null && s.trim() !== '');
  }

  @Action(PartnerItemLoad)
  getPartnerDetails(
    ctx: StateContext<PartnerItemStateModel>,
    { payload }: PartnerItemLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.partnerItemService.getPartnerDetails(payload).pipe(
      take(1),
      map((partnerDetails: PartnerModel) => {
        ctx.patchState({ ...partnerDetails, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(PartnerItemApprove)
  approvePartner(
    ctx: StateContext<PartnerItemStateModel>,
    { payload }: PartnerItemApprove,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.partnerItemService.approvePartner(payload).pipe(
      take(1),
      map((partnerDetails: PartnerModel) => {
        this.notificationService.success('Partner approved');
        ctx.patchState({ ...partnerDetails, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) =>
        of(this.notificationService.error(error.message || 'Something went wrong')),
      ),
    );
  }

  @Action(PartnerItemCreate)
  createPartner(
    ctx: StateContext<PartnerItemStateModel>,
    { payload }: PartnerItemCreate,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.partnerItemService.createPartner(payload).pipe(
      take(1),
      map(() => {
        this.notificationService.success('api.partner.success.create');
        this.navigateUserService.navigateUserToAbsolutePath('/dashboard/administration/partners');
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(PartnerItemUpdate)
  updatePartner(
    ctx: StateContext<PartnerItemStateModel>,
    { payload }: PartnerItemUpdate,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.partnerItemService.updatePartner(payload).pipe(
      take(1),
      map((partnerDetails: PartnerModel) => {
        this.notificationService.success('api.partner.success.update');
        ctx.patchState({ ...partnerDetails, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(PartnerItemDelete)
  deletePartner(
    ctx: StateContext<PartnerItemStateModel>,
    { payload }: PartnerItemDelete,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | Observable<void>> {
    return this.partnerItemService.deletePartners(payload.partnerIds).pipe(
      take(1),
      map(() => {
        payload.dialog.close();
        this.notificationService.success('api.partner.success.delete');
        this.navigateUserService.navigateUserToAbsolutePath('/dashboard/administration/partners');
        return ctx.dispatch(new PartnerItemInitialState());
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(PartnerItemInitialState)
  setInitialState(ctx: StateContext<PartnerItemStateModel>): void {
    ctx.setState(initialState);
  }

  @Action(CarrierShortNamesLoad)
  getCarrierShortNames(
    ctx: StateContext<PartnerItemStateModel>,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | string[]> {
    return this.partnerItemService.getCarrierShortNames().pipe(
      tap((returnData: string[]) => {
        ctx.patchState({
          shortNames: returnData,
        });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }
}
